import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance, { getApiBaseUrl } from "../content/axiosConfig";
import showErrorToast, { showSuccessToast } from "../toast/toast";

export const MAX_DETAIL_GALLERY_IMAGES = 30;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png";

export function resolveUploadUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl).trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s) || s.startsWith("data:")) return s;
  const base = getApiBaseUrl().replace(/\/$/, "");
  return `${base}${s.startsWith("/") ? s : `/${s}`}`;
}

/**
 * Session-level gallery for a sampling detail (PondDID) — not tied to fish samples.
 */
export default function SamplingDetailGallery({
  pondDetailId,
  canEdit = true,
  onChanged,
}) {
  const [images, setImages] = useState([]);
  const [maxImages, setMaxImages] = useState(MAX_DETAIL_GALLERY_IMAGES);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [expandedUrl, setExpandedUrl] = useState("");
  const inputRef = useRef(null);

  const remaining = Math.max(0, maxImages - images.length);

  const loadImages = useCallback(async () => {
    if (!pondDetailId) {
      setImages([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/api/PondDetail/${pondDetailId}/gallery`);
      setImages(res.data?.images || []);
      if (res.data?.maxImages) setMaxImages(res.data.maxImages);
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load gallery");
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [pondDetailId]);

  useEffect(() => {
    loadImages();
    setExpandedUrl("");
  }, [loadImages]);

  const notifyChanged = (imageCount) => {
    if (typeof onChanged === "function") {
      onChanged({ pondDetailId, imageCount });
    }
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length || !pondDetailId) return;

    if (remaining <= 0) {
      showErrorToast(`Maximum of ${maxImages} images in this gallery`);
      return;
    }

    const accepted = [];
    for (const file of files) {
      const okType =
        /image\/(jpeg|jpg|png)/i.test(file.type) ||
        /\.(jpe?g|png)$/i.test(file.name || "");
      if (!okType) {
        showErrorToast(`${file.name || "File"}: only JPEG and PNG are allowed`);
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        showErrorToast(`${file.name || "File"}: must be 5 MB or smaller`);
        continue;
      }
      accepted.push(file);
    }

    const toUpload = accepted.slice(0, remaining);
    if (!toUpload.length) return;
    if (accepted.length > remaining) {
      showErrorToast(`Only ${remaining} more image(s) can be added (limit ${maxImages})`);
    }

    setUploading(true);
    const formData = new FormData();
    toUpload.forEach((f) => formData.append("images", f));
    try {
      const res = await axiosInstance.post(`/api/PondDetail/${pondDetailId}/gallery`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (Array.isArray(res.data?.images)) {
        setImages(res.data.images);
      } else {
        await loadImages();
      }
      showSuccessToast(
        toUpload.length === 1 ? "Image uploaded" : `${toUpload.length} images uploaded`
      );
      notifyChanged(res.data?.imageCount ?? toUpload.length);
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to upload images");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async (imageId) => {
    if (!canEdit || !pondDetailId) return;
    try {
      const res = await axiosInstance.delete(
        `/api/PondDetail/${pondDetailId}/gallery/${imageId}`
      );
      setImages(res.data?.images || []);
      notifyChanged(res.data?.imageCount ?? 0);
      if (expandedUrl) setExpandedUrl("");
      showSuccessToast("Image removed");
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to delete image");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (!canEdit || uploading || remaining <= 0) return;
    uploadFiles(e.dataTransfer?.files);
  };

  if (!pondDetailId) {
    return <div className="text-muted small p-3">No sampling session selected.</div>;
  }

  return (
    <div className="sampling-detail-gallery">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div className="fw-semibold text-dark">
          Gallery{" "}
          <span className="badge bg-secondary-subtle text-secondary border ms-1">
            {images.length}/{maxImages}
          </span>
        </div>
        {loading && <span className="small text-muted">Loading…</span>}
        {uploading && <span className="small text-primary">Uploading…</span>}
      </div>

      {canEdit && remaining > 0 && (
        <div
          role="button"
          tabIndex={0}
          className={`sampling-image-dropzone mb-3 ${dragOver ? "is-dragover" : ""} ${
            uploading ? "is-disabled" : ""
          }`}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragOver(false);
          }}
          onDrop={onDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            className="d-none"
            disabled={uploading}
            onChange={(e) => uploadFiles(e.target.files)}
          />
          <div className="text-center px-2 py-4">
            <i className="fa fa-cloud-upload-alt fa-2x text-primary mb-2 d-block" aria-hidden />
            <div className="fw-medium text-dark">Drop images here or click to browse</div>
            <div className="small text-muted mt-1">
              JPEG or PNG · up to 5 MB each · {remaining} slot{remaining === 1 ? "" : "s"} left
            </div>
          </div>
        </div>
      )}

      {canEdit && remaining <= 0 && (
        <p className="small text-muted mb-2">
          Gallery limit reached ({maxImages}). Remove one to add more.
        </p>
      )}

      {!loading && images.length === 0 && (
        <p className="small text-muted mb-0">No photos for this sampling session yet.</p>
      )}

      {images.length > 0 && (
        <div className="sampling-image-grid">
          {images.map((img) => {
            const src = resolveUploadUrl(img.url);
            return (
              <div key={img.id} className="sampling-image-tile">
                <button
                  type="button"
                  className="sampling-image-tile__thumb"
                  onClick={() => setExpandedUrl(src === expandedUrl ? "" : src)}
                  title="View larger"
                >
                  <img src={src} alt={img.fileName || "Gallery"} />
                </button>
                {canEdit && (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger sampling-image-tile__delete"
                    title="Remove image"
                    aria-label="Remove image"
                    onClick={() => handleDelete(img.id)}
                  >
                    <i className="fa fa-trash" aria-hidden />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {expandedUrl && (
        <div className="sampling-image-expand mt-3">
          <img src={expandedUrl} alt="Enlarged gallery" />
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary mt-2"
            onClick={() => setExpandedUrl("")}
          >
            Close preview
          </button>
        </div>
      )}

      <style>{`
        .sampling-image-dropzone {
          border: 2px dashed #9bb8b6;
          border-radius: 12px;
          background: #f3f8f7;
          cursor: pointer;
          transition: border-color 0.15s ease, background 0.15s ease;
        }
        .sampling-image-dropzone:hover,
        .sampling-image-dropzone.is-dragover {
          border-color: #2b7a78;
          background: #e7f2f1;
        }
        .sampling-image-dropzone.is-disabled {
          opacity: 0.65;
          pointer-events: none;
        }
        .sampling-image-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          gap: 12px;
        }
        .sampling-image-tile {
          position: relative;
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid #d5e3e1;
          background: #fff;
          aspect-ratio: 1;
        }
        .sampling-image-tile__thumb {
          display: block;
          width: 100%;
          height: 100%;
          padding: 0;
          border: 0;
          background: #eef4f3;
          cursor: zoom-in;
        }
        .sampling-image-tile__thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .sampling-image-tile__delete {
          position: absolute;
          top: 4px;
          right: 4px;
          padding: 2px 6px;
          line-height: 1.2;
        }
        .sampling-image-expand {
          text-align: center;
          padding: 12px;
          border-radius: 12px;
          background: #f3f8f7;
          border: 1px solid #d5e3e1;
        }
        .sampling-image-expand img {
          max-width: 100%;
          max-height: 420px;
          object-fit: contain;
          border-radius: 8px;
        }
      `}</style>
    </div>
  );
}
